import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { InteractionType, IPublicClientApplication } from "@azure/msal-browser";
import { Query, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

import PageLayout from "./layouts/PageLayout";
import { lazy, Suspense } from "react";
import Splash from "./components/Splash/Views/Splash";
import RolesAuthRoute from "./utils/RolesAuthRoute";
import { usePerformanceObserver } from './hooks/usePerformanceObserver';

type AppProps = {
  pca: IPublicClientApplication;
};

const queryClient = new QueryClient();

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const Home = lazy(() => import("./pages/Views/Home"));
const UserManagement = lazy(() => import("./components/UserManagement/Views/UserManagement"));

const App = ({ pca }: AppProps) => {
  usePerformanceObserver();
  return (
    <Suspense fallback={<Splash />}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query: Query) => Boolean(query.meta?.persist)
          }
        }}
      >
        <MsalProvider instance={pca}>
          <BrowserRouter>
            <MsalAuthenticationTemplate
              interactionType={InteractionType.Redirect}
            >
              <Splash />
              <PageLayout>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <RolesAuthRoute roles={[]} route="Home">
                        <Home />
                      </RolesAuthRoute>
                    }
                  />
                  <Route
                    path="/UserManagement"
                    element={
                      <RolesAuthRoute
                        roles={["Account Owner"]}
                        route="UserManagement"
                      >
                        <UserManagement />
                      </RolesAuthRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </PageLayout>
            </MsalAuthenticationTemplate>
          </BrowserRouter>
        </MsalProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </PersistQueryClientProvider>
    </Suspense>
  );
};

export default App;
